export interface TrackSelection<TTrack> {
  index: number;
  track: TTrack;
  reason?: string;
}

export interface CrossfadeRequest<TTrack, TPlaylist> {
  primaryAudio: HTMLAudioElement;
  secondaryAudio: HTMLAudioElement;
  playlist: TPlaylist;
  currentTrack: TTrack;
  currentIndex: number;
  durationSeconds: number;
  targetVolume: number;
  getTrackUrl: (track: TTrack) => string;
  selectNext: (startIndex: number) => TrackSelection<TTrack> | null;
  isAnnouncement: (track: TTrack) => boolean;
  isSameTrack: (left: TTrack, right: TTrack) => boolean;
  getTrackDuration: (track: TTrack) => number;
  onHandoff: (payload: {
    previousTrack: TTrack;
    nextTrack: TTrack;
    nextIndex: number;
    currentTime: number;
    duration: number;
  }) => void;
  onPreviousTrackCompleted: (track: TTrack) => void;
  onTrackFailed: (track: TTrack) => void;
  onNoPlayableTracks: (playlist: TPlaylist, reason: string) => void;
  onSuppressPrimaryPause: (suppress: boolean) => void;
  onLog?: (message: string, error?: unknown) => void;
}

export default class AudioPlaybackEngine<TTrack, TPlaylist> {
  private crossfadeTimer: ReturnType<typeof window.setInterval> | null = null;
  private safetyTimer: ReturnType<typeof window.setTimeout> | null = null;
  private metadataTimer: ReturnType<typeof window.setTimeout> | null = null;
  private completeHandoff: (() => void) | null = null;
  private transitioning = false;
  private fadeProgress = 0;
  private targetVolume = 1;

  isTransitioning(): boolean {
    return this.transitioning;
  }

  private getUsableDuration(mediaDuration: number, trackDuration: number): number {
    const normalizedMediaDuration = Number(mediaDuration);
    if (Number.isFinite(normalizedMediaDuration) && normalizedMediaDuration > 0) {
      return normalizedMediaDuration;
    }

    const normalizedTrackDuration = Number(trackDuration);
    if (Number.isFinite(normalizedTrackDuration) && normalizedTrackDuration > 0) {
      return normalizedTrackDuration;
    }

    return 0;
  }

  setVolume(primaryAudio: HTMLAudioElement | null, secondaryAudio: HTMLAudioElement | null, volume: number): void {
    const safeVolume = Math.max(0, Math.min(1, volume));
    this.targetVolume = safeVolume;

    if (primaryAudio) {
      primaryAudio.volume = this.transitioning
        ? safeVolume * (1 - this.fadeProgress)
        : safeVolume;
    }

    if (secondaryAudio && this.transitioning) {
      secondaryAudio.volume = safeVolume * this.fadeProgress;
    }
  }

  cleanup(primaryAudio?: HTMLAudioElement | null, secondaryAudio?: HTMLAudioElement | null, targetVolume = 1): void {
    if (this.crossfadeTimer) {
      window.clearInterval(this.crossfadeTimer);
      this.crossfadeTimer = null;
    }

    if (this.safetyTimer) {
      window.clearTimeout(this.safetyTimer);
      this.safetyTimer = null;
    }

    if (this.metadataTimer) {
      window.clearTimeout(this.metadataTimer);
      this.metadataTimer = null;
    }

    this.completeHandoff = null;
    this.transitioning = false;
    this.fadeProgress = 0;

    if (secondaryAudio) {
      try {
        secondaryAudio.pause();
        secondaryAudio.removeAttribute('src');
        secondaryAudio.load();
      } catch {
        // Cleanup should never affect the main player.
      }
    }

    if (primaryAudio) {
      primaryAudio.volume = targetVolume;
    }
  }

  completeNow(): boolean {
    if (!this.completeHandoff) {
      return false;
    }

    this.completeHandoff();
    return true;
  }

  startCrossfadeToNext(request: CrossfadeRequest<TTrack, TPlaylist>): boolean {
    const {
      primaryAudio,
      secondaryAudio,
      playlist,
      currentTrack,
      currentIndex,
      durationSeconds,
      targetVolume,
      getTrackUrl,
      selectNext,
      isAnnouncement,
      isSameTrack,
      getTrackDuration,
      onHandoff,
      onPreviousTrackCompleted,
      onTrackFailed,
      onNoPlayableTracks,
      onSuppressPrimaryPause,
      onLog
    } = request;

    if (
      this.transitioning ||
      !primaryAudio ||
      !secondaryAudio ||
      !currentTrack ||
      isAnnouncement(currentTrack) ||
      primaryAudio.paused
    ) {
      return false;
    }

    const currentDuration = this.getUsableDuration(
      primaryAudio.duration,
      getTrackDuration(currentTrack)
    );

    if (currentDuration <= durationSeconds + 4) {
      return false;
    }

    const currentPosition = Number.isFinite(primaryAudio.currentTime) ? primaryAudio.currentTime : 0;
    const remaining = currentDuration - currentPosition;
    if (remaining > durationSeconds || remaining <= 0) {
      return false;
    }

    const selection = selectNext(currentIndex);
    const nextTrack = selection?.track;
    const nextIndex = selection?.index;
    const nextUrl = nextTrack ? getTrackUrl(nextTrack) : '';

    if (
      !selection ||
      nextIndex === undefined ||
      !nextTrack ||
      isAnnouncement(nextTrack) ||
      !nextUrl ||
      isSameTrack(currentTrack, nextTrack)
    ) {
      return false;
    }

    this.transitioning = true;
    this.fadeProgress = 0;
    this.targetVolume = Math.max(0, Math.min(1, targetVolume));
    let settled = false;
    let handoffStarted = false;

    const finishCleanup = () => {
      if (this.crossfadeTimer) {
        window.clearInterval(this.crossfadeTimer);
        this.crossfadeTimer = null;
      }

      if (this.safetyTimer) {
        window.clearTimeout(this.safetyTimer);
        this.safetyTimer = null;
      }

      if (this.metadataTimer) {
        window.clearTimeout(this.metadataTimer);
        this.metadataTimer = null;
      }
    };

    const recover = (reason: string, error?: unknown) => {
      if (settled) return;
      settled = true;
      finishCleanup();
      onTrackFailed(nextTrack);

      const recoverySelection = selectNext(nextIndex);
      if (recoverySelection?.track) {
        onHandoff({
          previousTrack: currentTrack,
          nextTrack: recoverySelection.track,
          nextIndex: recoverySelection.index,
          currentTime: 0,
          duration: getTrackDuration(recoverySelection.track)
        });
      } else {
        onNoPlayableTracks(playlist, reason);
      }

      this.cleanup(primaryAudio, secondaryAudio, this.targetVolume);

      if (error && (error as DOMException)?.name !== 'NotAllowedError') {
        onLog?.('Crossfade handoff failed', error);
      }
    };

    const finishHandoff = () => {
      if (settled || handoffStarted) return;
      handoffStarted = true;
      finishCleanup();

      const handoffTime = Number.isFinite(secondaryAudio.currentTime) ? secondaryAudio.currentTime : 0;
      const handoffDuration = Number.isFinite(secondaryAudio.duration)
        ? secondaryAudio.duration
        : getTrackDuration(nextTrack);

      onSuppressPrimaryPause(true);
      primaryAudio.pause();
      primaryAudio.src = nextUrl;
      primaryAudio.load();
      primaryAudio.volume = this.targetVolume;

      const startPrimary = async () => {
        try {
          if (Number.isFinite(handoffTime) && handoffTime > 0) {
            primaryAudio.currentTime = Math.min(handoffTime, primaryAudio.duration || handoffTime);
          }

          await primaryAudio.play();
          if (settled) return;
          settled = true;
          const primaryDuration = this.getUsableDuration(primaryAudio.duration, handoffDuration);
          onHandoff({
            previousTrack: currentTrack,
            nextTrack,
            nextIndex,
            currentTime: primaryAudio.currentTime || handoffTime,
            duration: primaryDuration
          });
          onPreviousTrackCompleted(currentTrack);
        } catch (error) {
          recover('crossfade_handoff_failed', error);
          return;
        } finally {
          onSuppressPrimaryPause(false);
          this.cleanup(primaryAudio, secondaryAudio, this.targetVolume);
        }
      };

      if (primaryAudio.readyState >= 1) {
        void startPrimary();
      } else {
        this.metadataTimer = window.setTimeout(() => {
          recover('crossfade_metadata_timeout');
        }, 5000);

        primaryAudio.addEventListener('error', () => {
          recover('crossfade_media_error');
        }, { once: true });

        primaryAudio.addEventListener('loadedmetadata', () => {
          if (this.metadataTimer) {
            window.clearTimeout(this.metadataTimer);
            this.metadataTimer = null;
          }
          void startPrimary();
        }, { once: true });
      }
    };

    this.completeHandoff = finishHandoff;

    secondaryAudio.src = nextUrl;
    secondaryAudio.preload = 'auto';
    secondaryAudio.currentTime = 0;
    secondaryAudio.volume = 0;

    secondaryAudio.play()
      .then(() => {
        if (settled || handoffStarted || !this.transitioning) {
          return;
        }

        const startedAt = Date.now();
        const durationMs = Math.max(250, durationSeconds * 1000);

        this.safetyTimer = window.setTimeout(finishHandoff, durationMs + 750);
        this.crossfadeTimer = window.setInterval(() => {
          if (settled || !this.transitioning) {
            finishCleanup();
            return;
          }

          const progress = Math.min((Date.now() - startedAt) / durationMs, 1);
          this.fadeProgress = progress;
          primaryAudio.volume = this.targetVolume * (1 - progress);
          secondaryAudio.volume = this.targetVolume * progress;

          if (progress >= 1) {
            finishHandoff();
          }
        }, 100);
      })
      .catch((error) => {
        if (handoffStarted || settled || !this.transitioning) {
          return;
        }

        recover('crossfade_start_failed', error);
      });

    return true;
  }
}
